// Sugalarm — alarm-clock-mode state (cycle 145).
//
// Module-local store (the cycle-36 `alarmPreview.ts` pattern):
// device-local only, no server state, no localStorage — a reboot or
// reload starts outside clock mode and the schedule controller
// (`useAlarmClockMode`) re-enters within a minute if a window is in
// effect. Consumers subscribe via `useSyncExternalStore` (see
// `web/src/hooks/useAlarmClockMode.ts`).
//
// SAFETY: this store knows nothing about alarms. The controller reads
// episode state only to suspend dim. No alarm LOGIC is gated by clock
// mode — the one presentation exception (cycle 166): LowAlarmBanner /
// HighAlarmBanner read `active` to choose WHICH chrome the same
// episode state renders (full-bleed over the bedside face while
// freshly sounding vs the thin strip). Handlers, phases, audio, and
// the wire are untouched by this store.

export type ClockModeReason = "nav" | "manual" | "schedule";

export interface ClockModeState {
  active: boolean;
  reason: ClockModeReason | null;
  /** Manual sessions: epoch ms to auto-exit at; null = until stopped. */
  autoExitAt: number | null;
  /** Manual dim toggle for the session: null = follow the schedule
   *  window; true/false = user override. Reset on exit. */
  dimOverride: boolean | null;
  /** Set on a manual exit that happened inside a scheduled window so
   *  the controller doesn't immediately re-enter; cleared when the
   *  window ends. */
  suppressedUntilWindowEnd: boolean;
  /** Route to return to when the face was entered from another screen
   *  via the nav button. */
  priorPath: string | null;
}

const IDLE: ClockModeState = {
  active: false,
  reason: null,
  autoExitAt: null,
  dimOverride: null,
  suppressedUntilWindowEnd: false,
  priorPath: null,
};

let state: ClockModeState = IDLE;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function getClockModeState(): ClockModeState {
  return state;
}

export function subscribeClockMode(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function enterClockMode(
  reason: ClockModeReason,
  opts?: { durationMs?: number | null; priorPath?: string | null },
): void {
  state = {
    active: true,
    reason,
    autoExitAt:
      opts?.durationMs != null ? Date.now() + opts.durationMs : null,
    dimOverride: null,
    suppressedUntilWindowEnd: false,
    priorPath: opts?.priorPath ?? state.priorPath,
  };
  emit();
}

/** `manual: true` (the exit icon / Settings) suppresses scheduled
 *  re-entry until the current window ends; auto-exits (duration elapsed
 *  / window over) don't. */
export function exitClockMode(opts?: { manual?: boolean }): void {
  state = {
    ...IDLE,
    suppressedUntilWindowEnd: opts?.manual === true,
  };
  emit();
}

export function toggleClockDim(currentlyDim: boolean): void {
  if (!state.active) return;
  state = { ...state, dimOverride: !currentlyDim };
  emit();
}

/** Controller-only: clear the manual-exit suppression once the
 *  scheduled window has ended. */
export function clearClockSuppression(): void {
  if (!state.suppressedUntilWindowEnd) return;
  state = { ...state, suppressedUntilWindowEnd: false };
  emit();
}
